/*
 * Consolidated JS for every epi-password-*.liquid section.
 *
 * Static asset, not per-section {% javascript %} tags — found via a real
 * headless-browser test (2026-08-11) that the compiled per-template
 * section-script bundle Shopify serves for this password page
 * (compiled_assets/scripts.js) is frozen: its entire payload is still
 * wrapped in `if (__sections__["password-splash"])`, the section id from
 * this page's very first single-file build, from *before* the six-section
 * restructure. Since no section with that id exists anymore, that
 * condition is always false and NONE of the JS in that bundle — the
 * countdown timer, the SMS popup, the lookbook's click-to-detail, all of
 * it — has been executing at all, regardless of how many times the
 * underlying section files were confirmed correct via direct
 * `shopify theme push`/`theme pull` against Shopify's own servers. This
 * explains "countdown shows zeros" and "lookbook click does nothing"
 * precisely: the code was correct, deployed, and never ran.
 *
 * Diagnosed by launching a real Chromium instance (playwright-core driving
 * the system's installed Google Chrome — no MCP Playwright tool is
 * available in this environment) against the live password page and
 * reading `document.querySelector('[data-countdown]').__countdownInit`
 * (undefined — proving initCountdown was never called) plus the actual
 * fetched bytes of compiled_assets/scripts.js (2.6KB, entirely gated
 * behind the dead section id). The snippet-level bundle
 * (compiled_assets/snippet-scripts.js, which is where
 * snippets/epi-password-modal-engine.liquid's JS lives) is NOT affected —
 * it's current and working — so this is specifically a section-script
 * bundling problem on this template, a different asset than the CSS one
 * diagnosed and fixed earlier the same day. Loading this file explicitly
 * from layout/password.liquid (`<script src="{{ 'epi-password.js' | asset_url }}" defer>`)
 * bypasses that broken bundle entirely, the same fix already proven for
 * assets/epi-password.css.
 *
 * Each section below still guards against running twice (e.g. if Shopify's
 * theme-editor live-preview swaps a section's HTML via AJAX after a
 * settings change) and re-initializes on `shopify:section:load`.
 */

/* ---------- Countdown (sections/epi-password-countdown.liquid) ---------- */

(function () {
  function parseNaiveAsUtc(str) {
    if (!str) return null;
    var m = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!m) return null;
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0));
  }

  function tzOffsetMs(instant, timeZone) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    var parts = {};
    dtf.formatToParts(instant).forEach(function (p) {
      parts[p.type] = p.value;
    });
    var asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    return asUtc - instant.getTime();
  }

  function zonedToUtcMs(naiveUtcRepresentation, timeZone) {
    var guess = naiveUtcRepresentation.getTime();
    for (var i = 0; i < 2; i++) {
      var offset = tzOffsetMs(new Date(guess), timeZone);
      guess = naiveUtcRepresentation.getTime() - offset;
    }
    return guess;
  }

  function initCountdown(root) {
    if (root.__countdownInit) return;
    root.__countdownInit = true;

    var targetStr = root.dataset.target;
    var timeZone = root.dataset.timezone || 'UTC';
    var naive = parseNaiveAsUtc(targetStr);
    var row = root.querySelector('[data-countdown-row]');
    var expiredEl = root.querySelector('[data-countdown-expired]');
    var daysEl = root.querySelector('[data-countdown-days]');
    var hoursEl = root.querySelector('[data-countdown-hours]');
    var minutesEl = root.querySelector('[data-countdown-minutes]');
    var secondsEl = root.querySelector('[data-countdown-seconds]');

    if (!naive) {
      root.hidden = true;
      return;
    }

    var targetMs;
    try {
      targetMs = zonedToUtcMs(naive, timeZone);
    } catch (e) {
      targetMs = naive.getTime();
    }

    function pad(n) {
      return String(n).padStart(2, '0');
    }

    function tick() {
      var remaining = targetMs - Date.now();
      if (remaining <= 0) {
        row.hidden = true;
        expiredEl.hidden = false;
        expiredEl.textContent = root.dataset.expiredMessage || "WE'RE LIVE.";
        clearInterval(timer);
        return;
      }
      var days = Math.floor(remaining / 86400000);
      var hours = Math.floor((remaining % 86400000) / 3600000);
      var minutes = Math.floor((remaining % 3600000) / 60000);
      var seconds = Math.floor((remaining % 60000) / 1000);
      daysEl.textContent = pad(days);
      hoursEl.textContent = pad(hours);
      minutesEl.textContent = pad(minutes);
      secondsEl.textContent = pad(seconds);
    }

    tick();
    var timer = setInterval(tick, 1000);
  }

  function initAllCountdowns() {
    document.querySelectorAll('[data-countdown]').forEach(initCountdown);
  }

  initAllCountdowns();
  document.addEventListener('shopify:section:load', initAllCountdowns);
})();

/* ---------- Entry (sections/epi-password-entry.liquid) ---------- */

document.addEventListener('DOMContentLoaded', function () {
  var pwInput = document.querySelector('[data-modal="password"] [aria-invalid="true"]');
  if (pwInput && window.__pwModal) {
    window.__pwModal.open('password');
  }
});

/* ---------- SMS popup (sections/epi-password-sms.liquid) ---------- */
// Epilogue Round 11 root-cause finding (Playwright, live, 2026-08-20):
// Postscript's <postscript-embedded-form> renders its entire UI inside a
// native `mode: "open"` Shadow DOM. el.shadowRoot exists; el's own
// light-DOM childNodes stays permanently empty; no part="..." attributes
// are exposed anywhere inside it. Two consequences, both fixed below:
//
//   1. The previous readiness check watched `el.childNodes.length` via a
//      MutationObserver on `el` itself — the light-DOM tree — which can
//      never change, since Postscript never puts anything there. reveal()
//      was therefore reached exclusively through the old 5000ms safety-net
//      timeout, on every single load. That IS this round's "roughly 5
//      seconds, every time, nothing visible" symptom — not SDK fetch time
//      (the real form finishes rendering inside the shadow root in well
//      under ~1s, confirmed live) and not an SDK init delay. It was this
//      theme's own code gating visibility on a condition that could never
//      be true. Fixed by polling for `el.shadowRoot` to appear, then
//      observing *that* tree instead, with "ready" defined as "a real
//      <input> exists inside it" (checked live: the shadow root starts
//      with a couple of skeleton/style nodes before the real fields
//      render, so childNodes.length > 0 alone would fire too early).
//
//   2. No page-level CSS can style anything inside that shadow root either
//      — see assets/epi-password.css's "Postscript embedded-form brand
//      styling" comment for the two-round history of that dead CSS. Since
//      the root is `mode: "open"`, `el.shadowRoot` is reachable from here,
//      so injectShadowStyle() below appends one <style> element directly
//      INSIDE it once ready. That's real DOM content at that point, not a
//      selector reaching across a boundary, so it actually works — verified
//      live, including that it survives the form's own re-renders (typed a
//      phone number, re-read computed styles and confirmed the injected
//      rule and the hidden heading/subtext were both still in effect).
//
// A styled skeleton (.epi-pw-sms__skeleton, rendered in the section's
// markup) fills the loading gap instead of blank space; .epi-pw-sms__ready
// on the wrapper fades the skeleton out and the real form in via the
// shared --duration-long transition token (epi-password.css).
(function () {
  function injectShadowStyle(root, wrap) {
    if (root.querySelector('style[data-epi-injected]')) return;

    var style = document.createElement('style');
    style.setAttribute('data-epi-injected', 'true');
    // Reuses the same --epi-sms-* custom properties epi-password.css sets
    // on .epi-pw-sms__panel (via inline style, from this section's theme
    // settings) — custom properties, unlike normal rules, DO inherit
    // through shadow boundaries, so one set of settings drives both the
    // light-DOM CSS and this injected shadow-DOM CSS.
    style.textContent =
      // padding-left is deliberately untouched: intl-tel-input sets it
      // inline (e.g. "padding-left: 76px") to reserve room for its own
      // flag/dial-code button, which sits as a sibling positioned against
      // the input's left edge — overriding it (verified live) visually
      // collapses that reserved space and covers the flag button.
      'input[type="tel"] {' +
      '  border: 1px solid var(--epi-sms-input-text-color, #000000) !important;' +
      '  background: transparent !important;' +
      '  color: var(--epi-sms-input-text-color, #000000) !important;' +
      '  padding-top: 14px !important;' +
      '  padding-bottom: 14px !important;' +
      '  padding-right: 16px !important;' +
      '  text-transform: uppercase;' +
      '}' +
      'input[type="tel"]::placeholder, .floating-label {' +
      '  color: var(--epi-sms-input-placeholder-color, #999999) !important;' +
      '  text-transform: uppercase;' +
      '}' +
      'button[type="submit"] {' +
      '  background: var(--epi-sms-button-bg-color, #000000) !important;' +
      '  color: var(--epi-sms-button-text-color, #ffffff) !important;' +
      '  width: 100% !important;' +
      '  height: 55px !important;' +
      '  border: none !important;' +
      '  border-radius: 0 !important;' +
      '  font-weight: 700;' +
      '  text-transform: uppercase;' +
      '  letter-spacing: 0.02em;' +
      '}';
    root.appendChild(style);

    // Hide Postscript's own heading/subtext/legal text ONLY when this
    // section is actually rendering a themed replacement for it (see the
    // data-hide-* attributes in the section's Liquid) — a merchant who
    // blanks one of those settings gets Postscript's own copy back
    // instead of nothing.
    var hideRules = '';
    if (wrap.getAttribute('data-hide-heading') === 'true') hideRules += 'h2:nth-of-type(1) { display: none !important; }';
    if (wrap.getAttribute('data-hide-subtext') === 'true') hideRules += 'h2:nth-of-type(2) { display: none !important; }';
    if (wrap.getAttribute('data-hide-legal') === 'true') hideRules += 'small { display: none !important; }';
    if (hideRules) {
      var hideStyle = document.createElement('style');
      hideStyle.setAttribute('data-epi-injected-hide', 'true');
      hideStyle.textContent = hideRules;
      root.appendChild(hideStyle);
    }

    // Button label: applied once, here, rather than continuously
    // re-asserted via the observer — see the section schema's `button_label`
    // setting info text for why. If Postscript's own script swaps this
    // button's text later (e.g. to a "sending..." state during a real
    // submit), that later, real state is left to win.
    var label = wrap.getAttribute('data-sms-button-label');
    if (label) {
      var btn = root.querySelector('button[type="submit"]');
      if (btn) btn.textContent = label;
    }
  }

  function initSmsEmbed() {
    var wrap = document.querySelector('[data-ps-embed-wrap]');
    if (!wrap || wrap.__psInit) return;
    wrap.__psInit = true;

    var el = wrap.querySelector('postscript-embedded-form');
    if (!el) return; // custom embed override or the missing-IDs warning is showing instead

    var revealed = false;
    var shadowObserver = null;
    var pollId = null;

    function reveal(root) {
      if (revealed) return;
      revealed = true;
      if (shadowObserver) shadowObserver.disconnect();
      if (pollId) clearInterval(pollId);
      if (root) injectShadowStyle(root, wrap);
      wrap.classList.add('epi-pw-sms__ps-embed--ready');
    }

    function checkReady(root) {
      if (!revealed && root.querySelector('input')) reveal(root);
    }

    function watchShadowRoot(root) {
      checkReady(root);
      if (revealed) return;
      shadowObserver = new MutationObserver(function () {
        checkReady(root);
      });
      shadowObserver.observe(root, { childList: true, subtree: true });
    }

    if (el.shadowRoot) {
      watchShadowRoot(el.shadowRoot);
    } else {
      // The custom element may not have upgraded yet (SDK script still
      // loading) — shadowRoot attachment isn't a DOM mutation event, so it
      // can't be MutationObserver'd directly; poll briefly until it exists.
      pollId = setInterval(function () {
        if (el.shadowRoot) {
          clearInterval(pollId);
          pollId = null;
          watchShadowRoot(el.shadowRoot);
        }
      }, 50);
    }

    // Safety net: never leave the popup permanently blank if the SDK
    // fails to load or the component fails to render for any reason. Real
    // detection above normally wins in well under a second (verified
    // live) — this is a last resort only.
    setTimeout(function () {
      reveal(el.shadowRoot);
    }, 5000);
  }

  initSmsEmbed();
  document.addEventListener('shopify:section:load', initSmsEmbed);
})();

/* ---------- Sheet settle (lookbook + About) ----------
   .is-settled on a [data-modal] = its [data-modal-sheet] has finished
   sliding up. The frosted close/back buttons (and glass Notify me buttons)
   only blur from then on, and the close button's press transition comes
   back (assets/epi-password.css). Reduced motion has no animation to wait
   for; the timer covers an animation that never fires animationend
   (cancelled, tab hidden), and never settles while the slide still runs
   (slow first frame). */
(function () {
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initSettle() {
    document.querySelectorAll('[data-modal]').forEach(function (root) {
      var sheet = root.querySelector('[data-modal-sheet]');
      if (!sheet || root.__epiSettleInit) return;
      root.__epiSettleInit = true;

      var settleTimer = null;
      function settle() {
        clearTimeout(settleTimer);
        if (root.classList.contains('is-open')) root.classList.add('is-settled');
      }
      sheet.addEventListener('animationend', function (e) {
        if (e.target === sheet) settle();
      });

      var wasOpen = false;
      new MutationObserver(function () {
        var open = root.classList.contains('is-open');
        if (open === wasOpen) return;
        wasOpen = open;
        clearTimeout(settleTimer);
        if (!open) {
          root.classList.remove('is-settled');
        } else if (reducedMotion()) {
          settle();
        } else {
          settleTimer = setTimeout(function check() {
            var running = sheet.getAnimations && sheet.getAnimations().some(function (a) {
              return a.playState === 'running';
            });
            if (running) settleTimer = setTimeout(check, 100);
            else settle();
          }, 800);
        }
      }).observe(root, { attributes: true, attributeFilter: ['class'] });
    });
  }

  initSettle();
  document.addEventListener('shopify:section:load', initSettle);
})();

/* ---------- Shared blurred background (snippets/epi-pw-blur-layer.liquid) ----------
   Fills each [data-epi-blur] canvas with the background <video>'s current
   frame while its popup is visible, so the blurred background keeps moving.
   Same element, same decoded frames: no second download or decoder, and
   always in sync. The canvas is --epi-blur-scale of the viewport (CSS px,
   not device px) and CSS blurs it at radius x scale, then scales it back
   up, so the blur runs on ~1/16 of the pixels a full-screen
   backdrop-filter did. Redraws only when the video has a new frame
   (requestVideoFrameCallback; ~30fps rAF fallback). The canvas overscans
   the viewport by 3x the radius on every side, with the edge pixels
   stretched out, so the blur doesn't fade to dark at the screen edges
   (backdrop-filter clamps edges the same way). Desktop keeps the old
   backdrop-filter (--epi-blur-copy in assets/epi-password.css), and so
   does any layer with no playing video to copy (image/GIF background,
   video failed): .is-backdrop. Same if the copy comes out solid black:
   some Safari versions can't draw an HLS video into a canvas. Checked once
   by reading the canvas back (the video is same-origin; a cross-origin one
   can't be read and is trusted).
   The copy only has the video in it, so page elements marked
   [data-epi-blur-source] (the SMS form box) are painted on top of it as
   flat shapes: backgrounds, borders, and a faint band per line of text,
   which is all a blur this strong leaves of them. Collected once per open
   (and on resize), a few rectangles per frame. A layer with
   [data-epi-blur-no-sources] (section setting off) skips them and fades the
   real ones out while its popup is open, since the backdrop-filter path
   would still show them.
   ?lbtest=slidefreeze holds the blurred copy still until the sheet lands
   (the background video itself keeps playing), ?lbtest=backdrop forces the
   old backdrop-filter, for A/B on a real iPhone. */
(function () {
  var flags = (/[?&]lbtest=([^&#]*)/.exec(location.search) || [, ''])[1];
  var SLIDE_FREEZE = /(^|,)slidefreeze(,|$)/.test(flags);
  var FORCE_BACKDROP = /(^|,)backdrop(,|$)/.test(flags);

  var layers = [];
  var video = null;
  var looping = false;
  var copyBroken = false;
  var copyChecked = false;

  function num(style, name, fallback) {
    var v = parseFloat(style.getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }

  function videoUsable() {
    return !FORCE_BACKDROP && !copyBroken && video && !video.hidden && video.readyState >= 2 && video.videoWidth > 0;
  }

  function size(layer) {
    var style = getComputedStyle(layer.el);
    layer.copy = num(style, '--epi-blur-copy', 1) > 0;
    var scale = Math.min(1, Math.max(0.05, num(style, '--epi-blur-scale', 0.25)));
    var radius = num(style, '--epi-blur-radius', 12);
    var saturate = num(style, '--epi-blur-saturate', 1);
    var rect = layer.el.getBoundingClientRect();
    var margin = Math.ceil(radius * 3);
    var cw = Math.max(1, Math.round((rect.width + margin * 2) * scale));
    var ch = Math.max(1, Math.round((rect.height + margin * 2) * scale));
    var c = layer.canvas;
    if (c.width !== cw) c.width = cw;
    if (c.height !== ch) c.height = ch;
    c.style.width = cw + 'px';
    c.style.height = ch + 'px';
    c.style.left = -margin + 'px';
    c.style.top = -margin + 'px';
    c.style.transform = 'scale(' + 1 / scale + ')';
    c.style.filter = 'blur(' + radius * scale + 'px) saturate(' + saturate + ')';
    layer.scale = scale;
    layer.margin = margin;
    layer.rect = rect;
    layer.videoRect = video ? video.getBoundingClientRect() : null;
    var overlay = document.querySelector('.epi-pw-entry__overlay');
    layer.dim = overlay ? parseFloat(getComputedStyle(overlay).opacity) || 0 : 0;
    layer.sources = layer.showSources ? collectSources() : [];
  }

  function alpha(color) {
    var m = /rgba?\(([^)]+)\)/.exec(color);
    if (!m) return 0;
    var parts = m[1].split(/[\s,\/]+/).filter(Boolean);
    return parts.length > 3 ? parseFloat(parts[3]) : 1;
  }

  // Flat paint list (CSS px, viewport coords) for every visible
  // [data-epi-blur-source] subtree, open shadow roots included.
  function collectSources() {
    var list = [];
    document.querySelectorAll('[data-epi-blur-source]').forEach(function (root) {
      if (root.checkVisibility && !root.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return;
      var o = 1;
      for (var a = root.parentElement; a; a = a.parentElement) o *= parseFloat(getComputedStyle(a).opacity);
      collect(root, o, list);
    });
    return list;
  }

  function collect(el, opacity, list) {
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return;
    opacity *= parseFloat(cs.opacity);
    if (opacity < 0.02) return;
    var r = el.getBoundingClientRect();
    if (r.width && r.height) {
      if (alpha(cs.backgroundColor) > 0) list.push({ x: r.left, y: r.top, w: r.width, h: r.height, c: cs.backgroundColor, a: opacity });
      var t = parseFloat(cs.borderTopWidth) || 0;
      var ri = parseFloat(cs.borderRightWidth) || 0;
      var b = parseFloat(cs.borderBottomWidth) || 0;
      var l = parseFloat(cs.borderLeftWidth) || 0;
      if (t && alpha(cs.borderTopColor)) list.push({ x: r.left, y: r.top, w: r.width, h: t, c: cs.borderTopColor, a: opacity });
      if (b && alpha(cs.borderBottomColor)) list.push({ x: r.left, y: r.bottom - b, w: r.width, h: b, c: cs.borderBottomColor, a: opacity });
      if (l && alpha(cs.borderLeftColor)) list.push({ x: r.left, y: r.top, w: l, h: r.height, c: cs.borderLeftColor, a: opacity });
      if (ri && alpha(cs.borderRightColor)) list.push({ x: r.right - ri, y: r.top, w: ri, h: r.height, c: cs.borderRightColor, a: opacity });
    }
    var kids = el.shadowRoot ? el.shadowRoot.childNodes : el.childNodes;
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k.nodeType === 1) {
        collect(k, opacity, list);
      } else if (k.nodeType === 3 && /\S/.test(k.nodeValue)) {
        // Glyphs cover roughly a third of their line box.
        var range = document.createRange();
        range.selectNodeContents(k);
        var rects = range.getClientRects();
        for (var j = 0; j < rects.length; j++) {
          var q = rects[j];
          if (q.width && q.height) list.push({ x: q.left, y: q.top + q.height * 0.15, w: q.width, h: q.height * 0.7, c: cs.color, a: opacity * 0.35 });
        }
      }
    }
  }

  // object-fit: cover, centered, inside the video's own box, then the edge
  // rows/columns stretched out to the canvas edges.
  function draw(layer) {
    var ctx = layer.ctx;
    if (!ctx) return;
    var c = layer.canvas;
    var s = layer.scale;
    var vr = layer.videoRect;
    var bx = Math.round((vr.left - layer.rect.left + layer.margin) * s);
    var by = Math.round((vr.top - layer.rect.top + layer.margin) * s);
    var bw = Math.max(1, Math.round(vr.width * s));
    var bh = Math.max(1, Math.round(vr.height * s));
    var k = Math.max(bw / video.videoWidth, bh / video.videoHeight);
    var dw = video.videoWidth * k;
    var dh = video.videoHeight * k;
    try {
      ctx.save();
      ctx.beginPath();
      ctx.rect(bx, by, bw, bh);
      ctx.clip();
      ctx.drawImage(video, bx + (bw - dw) / 2, by + (bh - dh) / 2, dw, dh);
      ctx.restore();
      if (bx > 0) ctx.drawImage(c, bx, by, 1, bh, 0, by, bx, bh);
      if (bx + bw < c.width) ctx.drawImage(c, bx + bw - 1, by, 1, bh, bx + bw, by, c.width - bx - bw, bh);
      if (by > 0) ctx.drawImage(c, 0, by, c.width, 1, 0, 0, c.width, by);
      if (by + bh < c.height) ctx.drawImage(c, 0, by + bh - 1, c.width, 1, 0, by + bh, c.width, c.height - by - bh);
    } catch (e) {
      copyBroken = true;
      return;
    }
    // Before anything else is painted over the video.
    if (layer.drawn === 2) checkCopy(layer);
    // The background section's own overlay (the backdrop blur used to see
    // it), then the SMS form, which sits above that overlay on the page.
    if (layer.dim > 0) {
      ctx.fillStyle = 'rgba(0,0,0,' + layer.dim + ')';
      ctx.fillRect(0, 0, c.width, c.height);
    }
    if (layer.sources.length) {
      ctx.setTransform(s, 0, 0, s, (layer.margin - layer.rect.left) * s, (layer.margin - layer.rect.top) * s);
      layer.sources.forEach(function (p) {
        ctx.globalAlpha = p.a;
        ctx.fillStyle = p.c;
        ctx.fillRect(p.x, p.y, p.w, p.h);
      });
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
    }
  }

  function checkCopy(layer) {
    if (copyChecked || !(video.currentTime > 0)) return;
    copyChecked = true;
    try {
      var data = layer.ctx.getImageData(0, 0, layer.canvas.width, layer.canvas.height).data;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i] || data[i + 1] || data[i + 2]) return;
      }
      copyBroken = true;
    } catch (e) {}
  }

  function visibleLayers() {
    return layers.filter(function (l) { return !l.modal.hidden; });
  }

  function frozen(layer) {
    return SLIDE_FREEZE && layer.drawn && !layer.modal.classList.contains('is-settled');
  }

  function render() {
    var active = visibleLayers();
    var usable = videoUsable();
    var copying = false;
    active.forEach(function (l) {
      var on = usable && l.copy;
      l.el.classList.toggle('is-backdrop', !on);
      if (on) copying = true;
      if (on && !frozen(l)) {
        draw(l);
        l.drawn = (l.drawn || 0) + 1;
      }
    });
    // Keep polling while a layer could still start copying (video not
    // ready yet); stop once nothing visible wants a copy.
    return active.some(function (l) { return l.copy; }) && (copying || !copyBroken);
  }

  function loop() {
    if (!render()) {
      looping = false;
      return;
    }
    if (videoUsable() && video.requestVideoFrameCallback) {
      video.requestVideoFrameCallback(loop);
    } else {
      // Also covers a video that hasn't started yet: keep checking.
      setTimeout(function () { requestAnimationFrame(loop); }, 33);
    }
  }

  // Fade the real SMS form out while a popup that leaves it out is open.
  function syncHidden() {
    document.documentElement.classList.toggle('epi-blur-hide-sources', layers.some(function (l) {
      return !l.showSources && l.modal.classList.contains('is-open');
    }));
  }

  function start(layer) {
    size(layer);
    layer.drawn = 0;
    // Draw now, before the popup's first frame is painted (this runs from
    // a MutationObserver, i.e. before rendering), so it never opens blank.
    render();
    if (!looping) {
      looping = true;
      if (videoUsable() && video.requestVideoFrameCallback) video.requestVideoFrameCallback(loop);
      else requestAnimationFrame(loop);
    }
  }

  function initBlur() {
    video = document.querySelector('[data-epi-bg-video]');
    document.querySelectorAll('[data-epi-blur]').forEach(function (el) {
      if (el.__epiBlurInit) return;
      var modal = el.closest('[data-modal]');
      var canvas = el.querySelector('canvas');
      if (!modal || !canvas) return;
      el.__epiBlurInit = true;
      var layer = { el: el, modal: modal, canvas: canvas, ctx: canvas.getContext('2d', { alpha: false }), showSources: !el.hasAttribute('data-epi-blur-no-sources') };
      layers.push(layer);
      var wasVisible = !modal.hidden;
      new MutationObserver(function () {
        var visible = !modal.hidden;
        if (visible && !wasVisible) start(layer);
        wasVisible = visible;
        syncHidden();
      }).observe(modal, { attributes: true, attributeFilter: ['hidden', 'class'] });
      if (wasVisible) start(layer);
    });
  }

  window.addEventListener('resize', function () {
    visibleLayers().forEach(function (l) {
      size(l);
      if (l.copy && videoUsable()) draw(l);
    });
  });

  initBlur();
  document.addEventListener('shopify:section:load', initBlur);
})();

/* ---------- Lookbook (sections/epi-password-lookbook.liquid) ----------
   Grid <-> detail switching with animated hand-off, plus the detail view's
   PDP-identical gallery: same Swiper CDN build and the same options as the
   real PDP init in assets/scripts.js. Swiper is NOT loaded with the page,
   and never while the open animation is running (parsing ~155KB of JS
   mid-slide was the main-thread hitch on mobile) — it's fetched once the
   sheet has settled, or on hover/touch of a product card. The first grid
   images are warmed in the background after page load (and immediately on
   LOOKBOOK hover/touch) so the grid isn't still downloading when it opens.
   Hovering or touching a product card also starts that product's first
   gallery image downloading, so the detail view usually opens with it
   ready. */

(function () {
  // Exact version: a pinned jsDelivr URL is served immutable (1-year cache)
  // instead of the 12h-revalidated "@12" alias.
  var SWIPER_SRC = 'https://cdn.jsdelivr.net/npm/swiper@12.2.0/swiper-bundle.min.js';
  var WARM_CARDS = 9;
  var swiperPromise = null;

  function loadSwiper() {
    if (typeof window.Swiper !== 'undefined') return Promise.resolve();
    if (!swiperPromise) {
      swiperPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = SWIPER_SRC;
        s.async = true;
        s.onload = resolve;
        s.onerror = function () {
          swiperPromise = null;
          reject();
        };
        document.head.appendChild(s);
      });
    }
    return swiperPromise;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initLookbook() {
    var root = document.querySelector('[data-modal="lookbook"]');
    if (!root || root.__lookbookInit) return;
    root.__lookbookInit = true;

    var grid = root.querySelector('[data-lookbook-grid]');
    var panel = root.querySelector('[data-lookbook-panel]');
    var current = grid;
    var switching = false;

    // Images inside the display:none modal don't start downloading until it
    // opens (lazy) — start the first screen's worth early instead. Flipping
    // to eager is enough: hidden eager images are still fetched + decoded.
    var warmed = false;
    function warmGrid() {
      if (warmed) return;
      warmed = true;
      var imgs = root.querySelectorAll('.epi-pw-lb-card__image img');
      for (var i = 0; i < imgs.length && i < WARM_CARDS; i++) {
        imgs[i].loading = 'eager';
        // Decode ahead too, so the first frames of the slide-up aren't
        // spent decoding (iOS otherwise decodes on first paint).
        if (imgs[i].decode) imgs[i].decode().catch(function () {});
      }
    }

    document.querySelectorAll('[data-open-modal="lookbook"]').forEach(function (link) {
      link.addEventListener('pointerenter', warmGrid, { once: true });
      link.addEventListener('touchstart', warmGrid, { once: true, passive: true });
      link.addEventListener('focus', warmGrid, { once: true });
    });

    // Background warm-up after the page itself has finished, so it never
    // competes with the password page's own LCP / background video.
    function scheduleWarm() {
      setTimeout(function () {
        if (window.requestIdleCallback) requestIdleCallback(warmGrid, { timeout: 2000 });
        else warmGrid();
      }, 1500);
    }
    if (document.readyState === 'complete') scheduleWarm();
    else window.addEventListener('load', scheduleWarm, { once: true });

    // The background video keeps playing while the lookbook is open. It
    // used to be paused on mobile because the full-screen backdrop blur
    // re-blurred every video frame; the blur is now a small canvas copy
    // (see "Shared blurred background" below), so that cost is gone.

    // Price "Invert background" mode: a backdrop-filter: invert(1) layer
    // masked to the price's own glyphs (an SVG <text> mask matching the
    // computed font), so the digits show the inverse of whatever is behind
    // them — see assets/epi-password.css for why mix-blend-mode can't.
    // The theme font is a system stack, so SVG-as-mask renders the same
    // face. Unsupported browsers keep the plain-white fallback.
    var invertMode = root.classList.contains('epi-pw-lookbook--price-invert') &&
      !!window.CSS && (CSS.supports('backdrop-filter', 'invert(1)') || CSS.supports('-webkit-backdrop-filter', 'invert(1)'));

    function xmlEscape(s) {
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function buildInvert(scope) {
      if (!invertMode || !scope) return;
      scope.querySelectorAll('.epi-pw-lb-price').forEach(function (el) {
        if (el.__text == null) el.__text = el.textContent.trim();
        el.classList.add('is-inverted');
        var w = el.offsetWidth;
        var h = el.offsetHeight;
        if (!w || !h || el.__maskKey === w + 'x' + h) return;
        el.__maskKey = w + 'x' + h;

        var probe = document.createElement('span');
        probe.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
        el.appendChild(probe);
        var baseline = probe.offsetTop;
        el.removeChild(probe);

        var cs = getComputedStyle(el);
        var text = el.__text;
        if (cs.textTransform === 'uppercase') text = text.toUpperCase();
        var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + w + '" height="' + h + '">' +
          '<text x="0" y="' + baseline + '" font-family="' + cs.fontFamily.replace(/"/g, "'") + '"' +
          ' font-weight="' + cs.fontWeight + '" font-size="' + cs.fontSize + '"' +
          ' letter-spacing="' + (cs.letterSpacing === 'normal' ? 0 : cs.letterSpacing) + '"' +
          ' xml:space="preserve">' + xmlEscape(text) + '</text></svg>';

        var layer = el.querySelector('.epi-pw-lb-price__invert');
        if (!layer) {
          layer = document.createElement('span');
          layer.className = 'epi-pw-lb-price__invert';
          layer.setAttribute('aria-hidden', 'true');
          el.appendChild(layer);
        }
        layer.style.setProperty('--lb-price-mask', 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '")');
      });
    }

    if (invertMode) {
      var lastWidth = window.innerWidth;
      var resizeTimer = null;
      window.addEventListener('resize', function () {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          if (root.classList.contains('is-open')) buildInvert(current);
        }, 150);
      });
    }

    // Only act on a real open/close edge: classList writes re-fire this
    // observer even when nothing changes. (.is-settled is handled by the
    // shared "Sheet settle" block above.)
    var wasOpen = false;
    new MutationObserver(function () {
      var open = root.classList.contains('is-open');
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) {
        warmGrid();
        buildInvert(current);
        // After the sheet has settled (0.42s), not during it.
        setTimeout(function () {
          if (root.classList.contains('is-open')) loadSwiper().catch(function () {});
        }, 500);
      } else {
        // Back to the list for next time, once the close animation is done.
        setTimeout(function () {
          if (!root.classList.contains('is-open')) showView(grid, true);
        }, 260);
      }
    }).observe(root, { attributes: true, attributeFilter: ['class'] });

    function initGallery(detail) {
      var el = detail.querySelector('[data-lookbook-swiper]');
      if (!el) return;
      if (el.swiper) {
        el.swiper.update();
        return;
      }
      loadSwiper().then(function () {
        if (el.swiper || detail.hidden) return;
        var slides = el.querySelectorAll('.swiper-slide').length;
        new window.Swiper(el, {
          loop: slides > 1,
          slidesPerGroup: 1,
          speed: 350,
          touchRatio: 0.8,
          resistanceRatio: 0.85,
          longSwipesRatio: 0.5,
          freeMode: false,
          keyboard: { enabled: true },
          pagination: { el: el.querySelector('.swiper-pagination'), type: 'progressbar' }
        });
      }).catch(function () {});
    }

    function primeDetail(id) {
      var detail = root.querySelector('[data-lookbook-detail="' + id + '"]');
      if (!detail || detail.__primed) return;
      detail.__primed = true;
      var first = detail.querySelector('.swiper-slide img');
      if (first) first.loading = 'eager';
    }

    function showView(next, instant) {
      if (!next || next === current || switching) return;
      var prev = current;
      current = next;

      function swap() {
        if (prev) {
          prev.hidden = true;
          prev.classList.remove('is-leaving');
        }
        next.hidden = false;
        if (panel) panel.scrollTop = 0;
        buildInvert(next);
        if (next !== grid) {
          primeDetail(next.getAttribute('data-lookbook-detail'));
          initGallery(next);
        }
        if (!instant && !reducedMotion()) {
          next.classList.add('is-entering');
          next.addEventListener('animationend', function done(e) {
            if (e.target !== next) return;
            next.classList.remove('is-entering');
            next.removeEventListener('animationend', done);
          });
        }
        switching = false;
      }

      if (instant || reducedMotion() || !prev) {
        swap();
        return;
      }
      switching = true;
      prev.classList.add('is-leaving');
      setTimeout(swap, 160);
    }

    function onCardIntent(e) {
      var card = e.target.closest && e.target.closest('[data-lookbook-open]');
      if (!card) return;
      primeDetail(card.getAttribute('data-lookbook-open'));
      loadSwiper().catch(function () {});
    }
    root.addEventListener('pointerover', onCardIntent);
    root.addEventListener('touchstart', onCardIntent, { passive: true });

    root.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-lookbook-open]');
      if (openBtn) {
        var detail = root.querySelector('[data-lookbook-detail="' + openBtn.getAttribute('data-lookbook-open') + '"]');
        showView(detail);
        return;
      }
      if (e.target.closest('[data-lookbook-back]')) {
        showView(grid);
        return;
      }
      var acc = e.target.closest('[data-lookbook-accordion]');
      if (acc) {
        var expanded = acc.getAttribute('aria-expanded') === 'true';
        acc.setAttribute('aria-expanded', String(!expanded));
        var wrap = acc.nextElementSibling;
        if (wrap) wrap.classList.toggle('is-open', !expanded);
      }
    });
  }

  initLookbook();
  document.addEventListener('shopify:section:load', initLookbook);
})();

/* ---------- Lookbook A/B test mode (real-device stutter isolation) ----------
   /password?lbtest=hud              frame-time readout on each lookbook open
   /password?lbtest=hud,noblur       ...with the full-screen blur replaced by a plain dim
   Switches: noblur, lateblur (scrim blur only after the sheet lands),
   slidefreeze (blurred background still during the slide only), backdrop
   (old full-screen backdrop-filter blur), noglass (close/back buttons),
   noimg, noslide (fade instead of slide), stagger (old per-card entrance).
   The HUD also reports About opens. Matching CSS is at
   the end of assets/epi-password.css. No effect without the parameter. */
(function () {
  var m = /[?&]lbtest=([^&#]*)/.exec(location.search);
  if (!m) return;
  var flags = decodeURIComponent(m[1]).split(',').filter(Boolean);
  flags.forEach(function (f) { document.documentElement.classList.add('lbtest-' + f); });
  if (flags.indexOf('hud') === -1) return;

  var hud = document.createElement('div');
  hud.className = 'epi-pw-lbtest-hud';
  hud.textContent = 'lbtest: ' + flags.join(',') + '\nopen the lookbook';
  function mount() { document.body.appendChild(hud); }
  if (document.body) mount(); else document.addEventListener('DOMContentLoaded', mount);

  var opens = 0;
  function record() {
    opens++;
    var deltas = [];
    var start = performance.now();
    var last = start;
    function tick(t) {
      deltas.push(t - last);
      last = t;
      if (t - start < 1000) { requestAnimationFrame(tick); return; }
      var max = 0, slow = 0, vslow = 0;
      deltas.slice(1).forEach(function (d) {
        if (d > max) max = d;
        if (d > 20) slow++;
        if (d > 34) vslow++;
      });
      hud.textContent = 'lbtest: ' + flags.join(',') +
        '\nopen #' + opens + ': ' + deltas.length + ' frames in 1s' +
        '\nworst ' + Math.round(max) + 'ms  >20ms: ' + slow + '  >34ms: ' + vslow;
    }
    requestAnimationFrame(tick);
  }

  function watch() {
    document.querySelectorAll('[data-modal="lookbook"], [data-modal="about"]').forEach(function (root) {
      var wasOpen = false;
      new MutationObserver(function () {
        var open = root.classList.contains('is-open');
        if (open && !wasOpen) record();
        wasOpen = open;
      }).observe(root, { attributes: true, attributeFilter: ['class'] });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();

/* ---------- Locked viewport height (epi-password-entry.liquid's fixed
   background + epi-password-sms.liquid's full-screen wrapper) ----------
   Round 12 fix for three related mobile bugs: the SMS form cropping at the
   bottom, the background video/GIF visibly re-cropping ("revealing" more of
   itself), and the phone input's whole panel shifting position — all three
   turned out to share one cause. Several pieces of CSS here sized
   themselves off the *live* viewport height (vh/svh units, or height:100%
   under a position:fixed box), and on mobile Safari the live viewport
   height changes when the keyboard opens/closes, not just on a real resize
   or rotation. A size tied to that live value recomputes every time the
   keyboard toggles, which reads as cropping, a re-cropped background, and a
   shifting layout, depending on which element it hits.
   Fixed by capturing the height once into --epi-pw-vh and re-deriving every
   relevant size from that custom property instead of vh/svh directly. Only
   re-captured on an actual width change (real rotation/resize) — a
   height-only change is treated as the on-screen keyboard and intentionally
   left alone, so nothing downstream moves while it's open. */
(function () {
  function setLockedVh() {
    document.documentElement.style.setProperty('--epi-pw-vh', window.innerHeight + 'px');
  }

  // A height-only change is only the keyboard when a text field has focus.
  // Instagram/TikTok/etc. in-app browsers open the page in a sheet that is
  // still sliding up (or still settling its toolbars) when this first runs,
  // so innerHeight grows after load with no width change. Ignoring that left
  // the fixed background short and a white band at the bottom with the
  // white LOOKBOOK link invisible on it.
  function keyboardLikelyOpen() {
    var el = document.activeElement;
    if (!el) return false;
    var tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }

  var lastWidth = window.innerWidth;
  function handleResize() {
    if (window.innerWidth !== lastWidth) {
      lastWidth = window.innerWidth;
      setLockedVh();
    } else if (!keyboardLikelyOpen() && !document.body.classList.contains('epi-pw-modal-open')) {
      // Not while a popup is open: resizing the background re-renders the
      // full-screen blur behind it mid-animation.
      setLockedVh();
    }
  }

  setLockedVh();
  window.addEventListener('orientationchange', setLockedVh);
  window.addEventListener('resize', handleResize);
  // Some in-app webviews finish their slide-up without firing resize.
  window.addEventListener('load', handleResize);
  window.addEventListener('pageshow', handleResize);
  setTimeout(handleResize, 600);
  setTimeout(handleResize, 1500);
})();

/* ---------- Hide footer links while the SMS phone input is focused
   (sections/epi-password-sms.liquid + assets/epi-password.css) ---------- */
// See the matching CSS rule's comment in assets/epi-password.css for why:
// iOS Safari re-anchors fixed, bottom-offset elements to the top of the
// on-screen keyboard rather than the true viewport bottom, so the Lookbook/
// Order-tracking and About footer links visibly jumped up into the phone
// input while typing. Toggling this class is the trigger; the CSS (mobile
// only) does the actual hiding.
(function () {
  function initInputFocusHide() {
    var inputs = document.querySelectorAll('[id^="vip-phone-input-"]');
    inputs.forEach(function (input) {
      if (input.__epiFocusHideInit) return;
      input.__epiFocusHideInit = true;
      input.addEventListener('focus', function () {
        document.body.classList.add('epi-pw-input-focused');
      });
      input.addEventListener('blur', function () {
        document.body.classList.remove('epi-pw-input-focused');
      });
    });
  }

  initInputFocusHide();
  document.addEventListener('shopify:section:load', initInputFocusHide);
})();

/* ---------- Background video fallback (sections/epi-password-entry.liquid) ---------- */

(function () {
  function initBgVideoFallback() {
    var video = document.querySelector('[data-epi-bg-video]');
    if (!video || video.__epiBgVideoInit) return;
    video.__epiBgVideoInit = true;

    // The GIF (if one is set) is only fetched here, when the video can't
    // play — see sections/epi-password-entry.liquid.
    function fallbackToImage() {
      video.setAttribute('hidden', '');
      var bg = document.querySelector('[data-epi-bg]');
      var anim = bg && bg.getAttribute('data-epi-bg-anim');
      if (anim && !bg.classList.contains('is-animated')) {
        bg.style.setProperty('--epi-bg-anim', 'url("' + anim + '")');
        bg.classList.add('is-animated');
      }
    }

    var conn = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
    if (conn && (conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g')) {
      fallbackToImage();
      return;
    }

    video.addEventListener('error', fallbackToImage);
    video.play().catch(fallbackToImage);
  }

  document.addEventListener('DOMContentLoaded', initBgVideoFallback);
  document.addEventListener('shopify:section:load', initBgVideoFallback);
})();
